import { useState, useEffect } from "react";
import { Textarea } from "../ui/textarea";

export interface ProjectItem {
  id: string;
  title_en: string;
  title_tr: string;
  date: string;
  version: string;
  tags_en: string;
  tags_tr: string;
  summary_en: string;
  summary_tr: string;
  detailed_content_en: string;
  detailed_content_tr: string;
  tech_stack: string;
  links: string;
  images: string;
  metrics: string;
  sort_order: number;
}

interface ProjectTitleRowProps {
  titleEn: string;
  setTitleEn: (v: string) => void;
  titleTr: string;
  setTitleTr: (v: string) => void;
}

function ProjectTitleRow({ titleEn, setTitleEn, titleTr, setTitleTr }: ProjectTitleRowProps) {
  return (
    <tr className="hover:bg-muted/5 transition-colors">
      <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Project Title</td>
      <td className="px-3 py-2 border-r border-border/40">
        <Textarea
          value={titleEn}
          onChange={(e) => setTitleEn(e.target.value)}
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          placeholder="e.g. Portfolio Website"
        />
      </td>
      <td className="px-3 py-2">
        <Textarea
          value={titleTr}
          onChange={(e) => setTitleTr(e.target.value)}
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          placeholder="örn. Portfolyo Sitesi"
        />
      </td>
    </tr>
  );
}

interface ProjectSharedRowsProps {
  date: string;
  setDate: (v: string) => void;
  version: string;
  setVersion: (v: string) => void;
  techStack: string;
  setTechStack: (v: string) => void;
}

function ProjectSharedRows({ date, setDate, version, setVersion, techStack, setTechStack }: ProjectSharedRowsProps) {
  return (
    <>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Date</td>
        <td colSpan={2} className="px-3 py-2">
          <Textarea
            placeholder="e.g. May 2026 / 2026 - Still"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Version</td>
        <td colSpan={2} className="px-3 py-2">
          <Textarea
            placeholder="e.g. v1.0.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Tech Stack</td>
        <td colSpan={2} className="px-3 py-2">
          <Textarea
            placeholder="e.g. React, Bun, SQLite, Tailwind"
            value={techStack}
            onChange={(e) => setTechStack(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          />
        </td>
      </tr>
    </>
  );
}

interface ProjectTagsRowProps {
  localTagsEn: string;
  setLocalTagsEn: (v: string) => void;
  localTagsTr: string;
  setLocalTagsTr: (v: string) => void;
  onBlurEn: () => void;
  onBlurTr: () => void;
}

function ProjectTagsRow({
  localTagsEn, setLocalTagsEn,
  localTagsTr, setLocalTagsTr,
  onBlurEn, onBlurTr
}: ProjectTagsRowProps) {
  return (
    <tr className="hover:bg-muted/5 transition-colors">
      <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Tags (Comma-separated)</td>
      <td className="px-3 py-2 border-r border-border/40">
        <Textarea
          placeholder="e.g. Personal, Open Source"
          value={localTagsEn}
          onChange={(e) => setLocalTagsEn(e.target.value)}
          onBlur={onBlurEn}
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
        />
      </td>
      <td className="px-3 py-2">
        <Textarea
          placeholder="e.g. Kişisel, Açık Kaynak"
          value={localTagsTr}
          onChange={(e) => setLocalTagsTr(e.target.value)}
          onBlur={onBlurTr}
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
        />
      </td>
    </tr>
  );
}

interface ProjectSummaryRowProps {
  summaryEn: string;
  setSummaryEn: (v: string) => void;
  summaryTr: string;
  setSummaryTr: (v: string) => void;
}

function ProjectSummaryRow({ summaryEn, setSummaryEn, summaryTr, setSummaryTr }: ProjectSummaryRowProps) {
  return (
    <tr className="hover:bg-muted/5 transition-colors">
      <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">
        <div>Summary</div>
        <div className="text-[10px] text-muted-foreground mt-0.5">Max 200 chars</div>
      </td>
      <td className="px-3 py-2 border-r border-border/40">
        <div className="relative">
          <Textarea
            maxLength={200}
            value={summaryEn}
            onChange={(e) => setSummaryEn(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y pr-12 py-1.5 text-xs"
            placeholder="Short English summary..."
          />
          <span className="absolute right-2 top-2.5 text-[9px] font-bold text-muted-foreground">
            {summaryEn.length}/200
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="relative">
          <Textarea
            maxLength={200}
            value={summaryTr}
            onChange={(e) => setSummaryTr(e.target.value)}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y pr-12 py-1.5 text-xs"
            placeholder="Kısa Türkçe özet..."
          />
          <span className="absolute right-2 top-2.5 text-[9px] font-bold text-muted-foreground">
            {summaryTr.length}/200
          </span>
        </div>
      </td>
    </tr>
  );
}

interface ProjectContentRowProps {
  contentEn: string;
  setContentEn: (v: string) => void;
  contentTr: string;
  setContentTr: (v: string) => void;
}

function ProjectContentRow({ contentEn, setContentEn, contentTr, setContentTr }: ProjectContentRowProps) {
  return (
    <tr className="hover:bg-muted/5 transition-colors">
      <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Detailed Content</td>
      <td className="px-3 py-2 border-r border-border/40">
        <Textarea
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary text-xs resize-y min-h-[120px]"
          value={contentEn}
          onChange={(e) => setContentEn(e.target.value)}
          placeholder="English details..."
        />
      </td>
      <td className="px-3 py-2">
        <Textarea
          className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary text-xs resize-y min-h-[120px]"
          value={contentTr}
          onChange={(e) => setContentTr(e.target.value)}
          placeholder="Türkçe detaylar..."
        />
      </td>
    </tr>
  );
}

interface ProjectDetailsFormProps {
  project: Partial<ProjectItem>;
  updateField: (fields: Partial<ProjectItem>) => void;
  getParsedList: (jsonStr: string | undefined) => any[];
}

export function ProjectDetailsForm({ project, updateField, getParsedList }: ProjectDetailsFormProps) {
  const [localTagsEn, setLocalTagsEn] = useState("");
  const [localTagsTr, setLocalTagsTr] = useState("");

  useEffect(() => {
    setLocalTagsEn(getParsedList(project.tags_en).join(", "));
  }, [project.tags_en]);

  useEffect(() => {
    setLocalTagsTr(getParsedList(project.tags_tr).join(", "));
  }, [project.tags_tr]);

  return (
    <div className="overflow-x-auto border border-border/60 rounded-xl bg-card/30">
      <table className="min-w-full divide-y divide-border/40 text-sm">
        <thead className="bg-muted/40 font-semibold text-brand-secondary">
          <tr>
            <th className="px-4 py-2.5 text-left w-1/4 border-r border-border/40 text-xs uppercase tracking-wider">Field</th>
            <th className="px-4 py-2.5 text-left w-3/8 border-r border-border/40 text-xs uppercase tracking-wider">English (EN)</th>
            <th className="px-4 py-2.5 text-left w-3/8 text-xs uppercase tracking-wider">Turkish (TR)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40 text-left">
          <ProjectTitleRow
            titleEn={project.title_en || ""}
            setTitleEn={(v) => updateField({ title_en: v })}
            titleTr={project.title_tr || ""}
            setTitleTr={(v) => updateField({ title_tr: v })}
          />
          <ProjectSharedRows
            date={project.date || ""}
            setDate={(v) => updateField({ date: v })}
            version={project.version || ""}
            setVersion={(v) => updateField({ version: v })}
            techStack={project.tech_stack || ""}
            setTechStack={(v) => updateField({ tech_stack: v })}
          />
          <ProjectTagsRow
            localTagsEn={localTagsEn}
            setLocalTagsEn={setLocalTagsEn}
            localTagsTr={localTagsTr}
            setLocalTagsTr={setLocalTagsTr}
            onBlurEn={() =>
              updateField({
                tags_en: JSON.stringify(localTagsEn.split(",").map((s) => s.trim()).filter(Boolean))
              })
            }
            onBlurTr={() =>
              updateField({
                tags_tr: JSON.stringify(localTagsTr.split(",").map((s) => s.trim()).filter(Boolean))
              })
            }
          />
          <ProjectSummaryRow
            summaryEn={project.summary_en || ""}
            setSummaryEn={(v) => updateField({ summary_en: v })}
            summaryTr={project.summary_tr || ""}
            setSummaryTr={(v) => updateField({ summary_tr: v })}
          />
          <ProjectContentRow
            contentEn={project.detailed_content_en || ""}
            setContentEn={(v) => updateField({ detailed_content_en: v })}
            contentTr={project.detailed_content_tr || ""}
            setContentTr={(v) => updateField({ detailed_content_tr: v })}
          />
        </tbody>
      </table>
    </div>
  );
}
